import type { PipelineStage } from './pipeline-stages.js';

export const DEAL_TYPE_OPTIONS = [
  { value: 'land_flip', label: 'Land Flip' },
  { value: 'rural_subdivide', label: 'Rural Subdivide' },
  { value: 'development_potential', label: 'Development Potential' },
  { value: 'land_home_package', label: 'Land-Home Package' },
  { value: 'conservation', label: 'Conservation' },
  { value: 'free_house_strategy', label: 'Free House Strategy' },
] as const;

export type DealTypeValue = typeof DEAL_TYPE_OPTIONS[number]['value'];

export const SELLER_DISPOSITION_LABELS = [
  'New Lead - (Not Qualified)',
  'New Lead - (Fully Qualified)',
  'Discovery - (No Contact)',
  'Discovery - (Contact Made)',
  'Discovery - (Call Booked)',
  'Cold Follow Up - Bi-Weekly - (Auto-Nurture Loop)',
  'Cold Follow Up - Quarterly - (Auto-Nurture Loop)',
  'Interested - (Keep Setting)',
  'Need More Info',
  'Verbal Offer - (Needed or Delivered)',
  'Negotiation/Waiting',
  'Contract Needed - (Ready to Sign)',
  'Contract Sent - (Offer FUP)',
  'Active Contract - (Closing Prep)',
  'Closing - (DD Completed)',
  'Closed A-B',
  'Completed',
  'Pending/Sold',
  'Seller Rejected Offer (LTFU)',
  'Cancelled Contract',
  'Not A Good Fit (NAGF)',
  'Bad Lead - (Wrong Number / Wrong Person)',
  'Bad Lead - (Not Interested)',
] as const;

function recordFrom(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function clean(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function token(value: string) {
  return value
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

const DEAL_TYPE_ALIASES: Record<string, DealTypeValue | null> = {
  land_flip: 'land_flip',
  raw_land_flip: 'land_flip',
  landflip: 'land_flip',
  rural_subdivide: 'rural_subdivide',
  rural_subdivision: 'rural_subdivide',
  subdivision: 'rural_subdivide',
  subdivide: 'rural_subdivide',
  development_potential: 'development_potential',
  development: 'development_potential',
  land_home_package: 'land_home_package',
  landhome_package: 'land_home_package',
  conservation: 'conservation',
  free_house_strategy: 'free_house_strategy',
  freehouse_strategy: 'free_house_strategy',
  sfh: 'free_house_strategy',
  site_prep: null,
  siteprep: null,
};

const DEAL_TYPE_LABELS = Object.fromEntries(
  DEAL_TYPE_OPTIONS.map((option) => [option.value, option.label]),
) as Record<DealTypeValue, string>;

const DEAL_TYPE_FILTER_VALUES: Record<DealTypeValue, string[]> = {
  land_flip: ['land_flip', 'raw_land_flip', 'Land Flip', 'Raw Land Flip'],
  rural_subdivide: ['rural_subdivide', 'rural_subdivision', 'subdivision', 'Rural Subdivide', 'Rural Subdivision', 'Subdivision'],
  development_potential: ['development_potential', 'development', 'Development Potential', 'Development'],
  land_home_package: ['land_home_package', 'Land-Home Package', 'Land Home Package'],
  conservation: ['conservation', 'Conservation'],
  free_house_strategy: ['free_house_strategy', 'sfh', 'Free House Strategy'],
};

const SELLER_DISPOSITION_ALIASES: Record<string, string> = Object.fromEntries(
  SELLER_DISPOSITION_LABELS.map((label) => [token(label), label]),
);

const SELLER_DISPOSITION_PIPELINE_STAGES: Record<string, PipelineStage> = {
  'New Lead - (Not Qualified)': 'discovered',
  'New Lead - (Fully Qualified)': 'discovered',
  'Discovery - (No Contact)': 'contacted',
  'Discovery - (Contact Made)': 'contacted',
  'Discovery - (Call Booked)': 'contacted',
  'Cold Follow Up - Bi-Weekly - (Auto-Nurture Loop)': 'engaged',
  'Cold Follow Up - Quarterly - (Auto-Nurture Loop)': 'engaged',
  'Interested - (Keep Setting)': 'engaged',
  'Need More Info': 'engaged',
  'Verbal Offer - (Needed or Delivered)': 'engaged',
  'Negotiation/Waiting': 'engaged',
  'Contract Needed - (Ready to Sign)': 'engaged',
  'Contract Sent - (Offer FUP)': 'engaged',
  'Active Contract - (Closing Prep)': 'under_contract',
  'Closing - (DD Completed)': 'under_contract',
  'Closed A-B': 'closed_won',
  Completed: 'closed_won',
  'Pending/Sold': 'closed_lost',
  'Seller Rejected Offer (LTFU)': 'closed_lost',
  'Cancelled Contract': 'closed_lost',
  'Not A Good Fit (NAGF)': 'closed_lost',
  'Bad Lead - (Wrong Number / Wrong Person)': 'closed_lost',
  'Bad Lead - (Not Interested)': 'closed_lost',
};

const GENERIC_STATUS_TOKENS = new Set([
  'active',
  'archived',
  'closed',
  'complete',
  'inactive',
  'lead',
  'new',
  'open',
  'pending',
]);

export function normalizeDealTypeValue(value: unknown): DealTypeValue | null {
  const cleaned = clean(value);
  if (!cleaned) return null;
  return DEAL_TYPE_ALIASES[token(cleaned)] ?? null;
}

export function dealTypeLabel(value: DealTypeValue) {
  return DEAL_TYPE_LABELS[value];
}

export function dealTypeFilterValues(value: DealTypeValue) {
  return DEAL_TYPE_FILTER_VALUES[value];
}

export function dealTypeFromSnapshot(snapshotValue: unknown, assetClass?: unknown): DealTypeValue | null {
  const snapshot = recordFrom(snapshotValue);
  const laAi = recordFrom(snapshot.laAi);
  const aiAnalysis = recordFrom(snapshot.aiAnalysis);
  const dealTypeConfig = recordFrom(snapshot.dealTypeConfig);
  const candidates = [
    snapshot.dealTypeKey,
    snapshot.dealType,
    snapshot.deal_type,
    snapshot.assetType,
    snapshot.asset_type,
    snapshot.strategy,
    snapshot.dealStrategy,
    snapshot.assetStrategy,
    snapshot.dealCategory,
    laAi.dealType,
    laAi.deal_type,
    aiAnalysis.dealType,
    aiAnalysis.deal_type,
    dealTypeConfig.dealType,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeDealTypeValue(candidate);
    if (normalized) return normalized;
  }

  const asset = clean(assetClass);
  if (asset === 'sfh') return 'free_house_strategy';
  if (asset === 'conservation') return 'conservation';
  return null;
}

export function dealTypeLabelFromSnapshot(snapshotValue: unknown, assetClass?: unknown) {
  const normalized = dealTypeFromSnapshot(snapshotValue, assetClass);
  return normalized ? dealTypeLabel(normalized) : null;
}

function normalizeKnownSellerDisposition(value: unknown) {
  const cleaned = clean(value);
  if (!cleaned) return null;
  return SELLER_DISPOSITION_ALIASES[token(cleaned)] ?? null;
}

function normalizeSellerDisposition(value: unknown, allowUnknown: boolean) {
  const cleaned = clean(value);
  if (!cleaned) return null;
  const known = normalizeKnownSellerDisposition(cleaned);
  if (known) return known;
  if (GENERIC_STATUS_TOKENS.has(token(cleaned))) return null;
  return allowUnknown ? cleaned : null;
}

export function sellerDispositionFromSnapshot(snapshotValue: unknown) {
  const snapshot = recordFrom(snapshotValue);
  const leadData = recordFrom(snapshot.leadData);
  const dealStage = recordFrom(leadData.dealStage);
  const laAi = recordFrom(snapshot.laAi);
  const aiAnalysis = recordFrom(snapshot.aiAnalysis);

  const directCandidates = [
    snapshot.normalizedSellerDisposition,
    snapshot.normalized_seller_disposition,
    snapshot.sellerDisposition,
    snapshot.seller_disposition,
    snapshot.sourceSellerDisposition,
    snapshot.source_seller_disposition,
    snapshot.sellerDispositionLabel,
    dealStage.sellerDisposition,
    leadData.sellerDisposition,
    laAi.normalizedSellerDisposition,
    laAi.sellerDisposition,
    laAi.sourceSellerDisposition,
    aiAnalysis.normalizedSellerDisposition,
    aiAnalysis.sellerDisposition,
    aiAnalysis.sourceSellerDisposition,
  ];
  for (const candidate of directCandidates) {
    const normalized = normalizeSellerDisposition(candidate, true);
    if (normalized) return normalized;
  }

  const fallbackCandidates = [
    snapshot.status,
    snapshot.disposition,
    dealStage.status,
    leadData.status,
  ];
  for (const candidate of fallbackCandidates) {
    const normalized = normalizeSellerDisposition(candidate, false);
    if (normalized) return normalized;
  }

  return null;
}

export function pipelineStageFromSellerDisposition(value: unknown): PipelineStage | null {
  const normalized = normalizeKnownSellerDisposition(value);
  if (!normalized) return null;
  return SELLER_DISPOSITION_PIPELINE_STAGES[normalized] ?? null;
}
